import { ForgotPasswordModal } from "@/components/auth/forgot-password-modal";
import { UserAuth } from "@/components/auth/user-auth";
import { VerificationModal } from "@/components/auth/verification-modal";
import { useToast } from "@/components/ui/use-toast";
import { useLoginAuthStore } from "@/lib/hooks/use-login-auth-store";
import { useIntercom } from "@/lib/hooks/use-intercom";
import { useStytch } from "@stytch/react";
import { AuthenticateResponse } from "@stytch/vanilla-js";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { touchLastActivity } from "@/lib/inactivity";
import {
  getAuthenticationErrorMessage,
  getStytchErrorMessage,
} from "@/lib/utils/stytch-errors";

export const Route = createFileRoute("/_auth/login")({
  component: Login,
});

function Login() {
  const {
    otpResponse,
    phoneNumber,
    forgotPasswordModalOpen,
    verificationModalOpen,
    setOtpData,
    setOtpResponse,
    setPhoneNumber,
    setVerificationModalOpen,
    setForgotPasswordModalOpen,
    resetLoginState,
  } = useLoginAuthStore();

  const stytchClient = useStytch();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { toast } = useToast();
  const { openMessenger } = useIntercom();

  const handleAuthenticate = useCallback(
    async (email: string, password: string) => {
      let phoneNumber: string | null = null;

      try {
        const response: AuthenticateResponse =
          await stytchClient.passwords.authenticate({
            email,
            password,
            session_duration_minutes: 60 * 24, // 24 hours
          });

        touchLastActivity();

        // In development mode, do not require OTP
        if (import.meta.env.VITE_REQUIRE_MFA !== "true") {
          // Invalidate user query to ensure user data is fetched
          await queryClient.invalidateQueries({
            queryKey: ["current-user"],
          });

          setTimeout(() => {
            router.navigate({
              to: "/",
            });
          }, 1000);
          return;
        }

        phoneNumber = response.user.phone_numbers[0].phone_number;
      } catch (error: unknown) {
        toast({
          title: "Authentication Failed",
          description: getAuthenticationErrorMessage(error),
          className:
            "bottom-0 left-0 flex fixed md:max-w-[420px] md:bottom-4 md:left-4 border-2 border-red-500 bg-red-50 text-red-900",
        });
        return;
      }

      try {
        if (!phoneNumber || phoneNumber.length === 0) {
          toast({
            title: "Authentication Error",
            description: "No phone number found for OTP verification",
            className:
              "bottom-0 left-0 flex fixed md:max-w-[420px] md:bottom-4 md:left-4 border-2 border-red-500 bg-red-50 text-red-900",
          });
          return;
        }

        const otpResponse = await stytchClient.otps.sms.send(phoneNumber, {
          expiration_minutes: 5,
        });

        // Set OTP data in the store
        setOtpData(otpResponse, phoneNumber);
      } catch (error: unknown) {
        toast({
          title: "Authentication Failed",
          description: getStytchErrorMessage(error),
          className:
            "bottom-0 left-0 flex fixed md:max-w-[420px] md:bottom-4 md:left-4 border-2 border-red-500 bg-red-50 text-red-900",
        });
        return;
      }
    },
    [stytchClient, queryClient, router, toast, setOtpData],
  );

  const handleForgotPassword = useCallback(
    async (email: string) => {
      await stytchClient.passwords.resetByEmailStart({
        email,
        reset_password_redirect_url: `${window.location.origin}/reset-password`,
        login_redirect_url: `${window.location.origin}/login`,
        reset_password_expiration_minutes: 15, // 15 minutes
      });
    },
    [stytchClient],
  );

  return (
    <>
      <div className="flex h-screen w-full flex-row bg-[#fbf4ea]">
        <div className="flex w-1/2 flex-col items-center justify-center">
          <div className="w-[520px]">
            <h1 className="text-4xl font-semibold leading-tight text-black">
              Welcome to Somethings
            </h1>
            <p className="mt-1 text-2xl font-semibold text-[#9a8f80]">
              Log in below
            </p>

            <div className="mt-10">
              <UserAuth onAuthenticate={handleAuthenticate} />

              <ForgotPasswordModal
                open={forgotPasswordModalOpen}
                onClose={() => setForgotPasswordModalOpen(false)}
                onResetPassword={handleForgotPassword}
              />
            </div>

            <div className="mt-6 flex flex-col items-center gap-3 text-sm text-[#9a8f80]">
              <div>
                Forgot your password?{" "}
                <button
                  type="button"
                  onClick={() => setForgotPasswordModalOpen(true)}
                  className="font-medium text-black hover:underline"
                >
                  Reset it here
                </button>
              </div>
              <div>
                Don&apos;t have a password?{" "}
                <Link
                  to="/register"
                  className="font-medium text-black hover:underline"
                >
                  Register here
                </Link>
              </div>
              <div>
                Need help logging in?{" "}
                <button
                  type="button"
                  onClick={openMessenger}
                  className="border-none bg-transparent p-0 font-medium text-black hover:underline"
                >
                  Contact support
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-1/2 items-center justify-center bg-[#fbf4ea]">
          <img
            src="assets/Illustrations-6.png"
            alt="Somethings illustration"
            className="h-auto w-full max-w-[520px]"
          />
        </div>
      </div>
      <VerificationModal
        open={verificationModalOpen}
        onClose={() => {
          setVerificationModalOpen(false);
          setOtpResponse(null);
          setPhoneNumber(null);
        }}
        otpResponse={otpResponse}
        setOtpResponse={setOtpResponse}
        phoneNumber={phoneNumber ?? ""}
        handleSuccess={() => {
          resetLoginState();
          queryClient.invalidateQueries({
            queryKey: ["current-user"],
          });

          setTimeout(() => {
            router.navigate({
              to: "/",
            });
          }, 1000);
        }}
      />
    </>
  );
}
